import {
  ApiError,
  type CatalogueItemResource,
  CatalogueItemsController_list_v1,
  type MachineImportResource,
  MachineImportsController_preview_v1,
  MachineImportsController_record_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCircleCheck,
  IconLoader2,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { EmptyState, Screen, ScreenFact, ScreenNote } from '../back-office/Screen';
import { type MappingDraft, MapProductCodeDialog } from '../billing-machines/MapProductCodeDialog';
import { machineBillsForImportPath, machineImportPath, paths } from '../paths';
import { Band, Bills, Days, FileRefused, Tally } from './ImportOutcome';
import { problemWithFile } from './machine-upload-file';

/**
 * Uploading a billing machine's `ORDERS.CSV`: pick, preview, confirm, and the
 * recorded answer. The same shape as `CatalogueUpload` — nothing is written
 * until the preview has been seen.
 *
 * **The `File` is sent as it is**, through the generated operations, which
 * send a raw body as bytes. Never `file.text()`: it strips a leading BOM, and
 * the stored file and its `fileSha256` would no longer be the machine's.
 * `problemWithFile` reads the text only to count rows.
 *
 * **Preview takes no key; the upload takes one per picked file.** A retry after
 * a lost answer replays under the same key. Picking a file again — even the
 * same one — is a new attempt with a new key, and is safe regardless: bills
 * already imported come back `ALREADY_IMPORTED` and nothing doubles.
 *
 * **A refused code is mapped here, without leaving (#238).** The `File` lives in
 * this component's state and nothing can re-pick it for the person, so
 * navigating to the machine's product codes threw the upload away — once per
 * unmapped code. The mapping form opens in place and the preview re-runs on the
 * file already in hand: the same pick, so `picks` does not move and the pending
 * record attempt keeps its key.
 */

type Stage =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'previewing'; readonly file: File }
  | { readonly kind: 'refused'; readonly file: File; readonly problem: string }
  | { readonly kind: 'failed'; readonly file: File; readonly error: unknown }
  | {
      readonly kind: 'previewed';
      readonly file: File;
      readonly preview: MachineImportResource;
      readonly recording: boolean;
      readonly error: unknown;
      /** A re-run after a mapping that failed. The mapping itself was saved. */
      readonly refreshError: unknown;
    }
  | { readonly kind: 'recorded'; readonly file: File; readonly result: MachineImportResource };

export function MachineUpload() {
  const client = useApiClient();

  const fileInputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>({ kind: 'waiting' });

  /** One per picked file; see the note at the head of this file. */
  const picks = useRef(0);
  const attempt = useRef<WriteAttempt | null>(null);

  /**
   * The mapping form, opened from a refused bill. The machine stays set once a
   * mapping has been opened, so the dialog stays mounted to animate shut and
   * the catalogue is read once rather than on every open.
   */
  const [mapMachineId, setMapMachineId] = useState<string | null>(null);
  const [mapDraft, setMapDraft] = useState<MappingDraft | null>(null);
  const catalogue = useCatalogueItems(mapMachineId !== null);

  const pick = useCallback(
    async (file: File) => {
      picks.current += 1;
      attempt.current = null;

      setStage({ kind: 'previewing', file });

      const problem = await problemWithFile(file);
      if (problem !== null) {
        setStage({ kind: 'refused', file, problem });
        return;
      }

      try {
        const preview = await client.request(MachineImportsController_preview_v1, {
          query: { fileName: file.name },
          body: file,
        });
        setStage({
          kind: 'previewed',
          file,
          preview,
          recording: false,
          error: null,
          refreshError: null,
        });
      } catch (error: unknown) {
        setStage({ kind: 'failed', file, error });
      }
    },
    [client],
  );

  /**
   * The same preview again, on the file already in state, after a code it named
   * was mapped. Not `pick`: that counts a new pick and drops the held attempt,
   * and this is the same file the person chose. The preview writes nothing and
   * carries no key, so re-running it cannot touch a pending upload.
   *
   * A failure here keeps the preview in hand rather than sending the person
   * back to a file input that cannot be re-filled — the mapping was saved
   * either way.
   */
  const refresh = useCallback(
    async (file: File, previous: MachineImportResource) => {
      setStage({ kind: 'previewing', file });

      try {
        const preview = await client.request(MachineImportsController_preview_v1, {
          query: { fileName: file.name },
          body: file,
        });
        setStage({
          kind: 'previewed',
          file,
          preview,
          recording: false,
          error: null,
          refreshError: null,
        });
      } catch (error: unknown) {
        setStage({
          kind: 'previewed',
          file,
          preview: previous,
          recording: false,
          error: null,
          refreshError: error,
        });
      }
    },
    [client],
  );

  const record = useCallback(
    async (file: File, preview: MachineImportResource) => {
      const sending = attemptFor(attempt.current, `pick-${String(picks.current)}`);
      attempt.current = sending;

      setStage({
        kind: 'previewed',
        file,
        preview,
        recording: true,
        error: null,
        refreshError: null,
      });

      try {
        const result = await client.request(MachineImportsController_record_v1, {
          query: { fileName: file.name },
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
          body: file,
        });
        attempt.current = null;
        setStage({ kind: 'recorded', file, result });
      } catch (error: unknown) {
        setStage({ kind: 'previewed', file, preview, recording: false, error, refreshError: null });
      }
    },
    [client],
  );

  const reset = useCallback(() => {
    attempt.current = null;
    setStage({ kind: 'waiting' });

    // A browser fires no `change` for the same file chosen twice.
    if (fileInput.current !== null) fileInput.current.value = '';
  }, []);

  const busy = stage.kind === 'previewing' || (stage.kind === 'previewed' && stage.recording);
  const shown =
    stage.kind === 'previewed' ? stage.preview : stage.kind === 'recorded' ? stage.result : null;

  return (
    <Screen
      title="Upload a machine file"
      lede="A billing machine's ORDERS.CSV"
      description="A billing machine's export, previewed before anything is written. Each bill becomes a cash sale on its own date at the outlet the machine was attached to. Uploading the same file again is safe: bills already imported are recognised and not counted twice."
      action={
        <Button variant="outline" nativeButton={false} render={<Link to={paths.machineUploads} />}>
          <IconArrowLeft data-icon="inline-start" />
          Back to the uploads
        </Button>
      }
      aside={
        <>
          {stage.kind !== 'waiting' && (
            <ScreenFact label="File">
              <span className="font-mono break-all">{stage.file.name}</span>
            </ScreenFact>
          )}

          {shown !== null && (
            <ScreenFact label="In the file">
              <span className="font-mono numeric">{shown.billCount}</span>{' '}
              {shown.billCount === 1 ? 'bill' : 'bills'} on{' '}
              <span className="font-mono numeric">{shown.rowCount}</span>{' '}
              {shown.rowCount === 1 ? 'row' : 'rows'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <label
              htmlFor={fileInputId}
              className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase"
            >
              Choose a file
            </label>

            {/* The browser's own control, as on `CatalogueUpload`. */}
            <input
              ref={fileInput}
              id={fileInputId}
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:text-foreground disabled:opacity-60"
              onChange={(event) => {
                const chosen = event.target.files?.[0];
                if (chosen !== undefined) void pick(chosen);
              }}
            />
          </div>
        </>
      }
    >
      {stage.kind === 'waiting' && (
        <div className="space-y-10">
          <div className="mx-auto max-w-md space-y-3 py-14 text-center">
            <EmptyState
              title="Choose a machine's ORDERS.CSV"
              description="You see every bill's outcome before anything is written."
            />
          </div>
          <WhatAFileIs />
        </div>
      )}

      {stage.kind === 'previewing' && (
        <p role="status" className="flex items-center gap-2 font-medium text-foreground">
          <IconLoader2 className="size-4 animate-spin" aria-hidden />
          Reading the file
        </p>
      )}

      {stage.kind === 'refused' && (
        <div className="space-y-10">
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{stage.problem}</AlertDescription>
          </Alert>
          <WhatAFileIs />
        </div>
      )}

      {stage.kind === 'failed' && (
        <div className="space-y-10">
          <FileRefused error={stage.error} />
          <WhatAFileIs />
        </div>
      )}

      {stage.kind === 'previewed' && (
        <div className="space-y-8">
          <Tally counts={stage.preview.counts} preview />

          {stage.refreshError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription className="space-y-3">
                <p>
                  The mapping was saved, but this preview could not be read again, so it is the one
                  from before. {messageForFailure(stage.refreshError)}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refresh(stage.file, stage.preview)}
                >
                  Read the preview again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Confirm
            preview={stage.preview}
            recording={stage.recording}
            error={stage.error}
            onConfirm={() => void record(stage.file, stage.preview)}
            onCancel={reset}
          />

          <Days days={stage.preview.days} preview />
          <Bills
            bills={stage.preview.bills}
            preview
            onMap={(machineId, draft) => {
              setMapMachineId(machineId);
              setMapDraft(draft);
            }}
          />
        </div>
      )}

      {stage.kind === 'recorded' && <Recorded result={stage.result} onAgain={reset} />}

      {mapMachineId !== null && (
        <MapProductCodeDialog
          machineId={mapMachineId}
          draft={mapDraft}
          items={catalogue.items}
          itemsFailed={catalogue.failed}
          onClose={() => setMapDraft(null)}
          onMapped={() => {
            setMapDraft(null);
            // The file is still in state; the refused bill moves out of the
            // band and any other unmapped code is still listed.
            if (stage.kind === 'previewed') void refresh(stage.file, stage.preview);
          }}
        />
      )}
    </Screen>
  );
}

/**
 * The catalogue items the mapping form offers — read the first time a mapping
 * is opened and kept, so an upload with nothing refused reads nothing. Retired
 * items are excluded, which is the API's own default: mapping a code to one is
 * refused `MACHINE_PRODUCT_ITEM_RETIRED`. `ProductCodes` reads them the same way.
 */
function useCatalogueItems(needed: boolean): {
  readonly items: readonly CatalogueItemResource[] | null;
  readonly failed: boolean;
} {
  const client = useApiClient();
  const [items, setItems] = useState<readonly CatalogueItemResource[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!needed) return;

    const controller = new AbortController();
    let live = true;

    collectPages<CatalogueItemResource>((offset, limit) =>
      client.request(CatalogueItemsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((value) => {
        if (live) setItems(value);
      })
      .catch(() => {
        if (live) setFailed(true);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, needed]);

  return { items, failed };
}

function Confirm({
  preview,
  recording,
  error,
  onConfirm,
  onCancel,
}: {
  preview: MachineImportResource;
  recording: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { created } = preview.counts;
  const refusedWhole = error instanceof ApiError && !error.retryable;

  return (
    <div className="space-y-4">
      {error !== null &&
        (refusedWhole ? (
          <FileRefused error={error} />
        ) : (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>
              {messageForFailure(error)} Uploading again is safe — it is the same attempt, and
              nothing is counted twice.
            </AlertDescription>
          </Alert>
        ))}

      <p className="text-sm text-muted-foreground">
        {created === 0
          ? 'Nothing in this file would be created. Uploading it still keeps the file and its outcomes on record.'
          : 'This is a preview and nothing has been written. A day closed since may still refuse its bills when you upload.'}
      </p>

      <div className="flex flex-wrap gap-3">
        <Button onClick={onConfirm} disabled={recording || refusedWhole}>
          {recording ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconUpload data-icon="inline-start" />
          )}
          {error !== null && !refusedWhole
            ? 'Try the upload again'
            : created === 0
              ? 'Upload the file'
              : `Upload and create ${String(created)} ${created === 1 ? 'bill' : 'bills'}`}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={recording}>
          Choose a different file
        </Button>
      </div>
    </div>
  );
}

function Recorded({ result, onAgain }: { result: MachineImportResource; onAgain: () => void }) {
  const { created, alreadyImported, refused } = result.counts;

  return (
    <div className="space-y-8">
      <Alert role="status" variant={refused > 0 ? 'destructive' : undefined}>
        {refused > 0 ? <IconAlertTriangle /> : <IconCircleCheck />}
        <AlertDescription>
          {refused === 0
            ? created === 0
              ? `Uploaded, and every bill in it was already imported — nothing was counted twice.`
              : `Uploaded. ${String(created)} ${created === 1 ? 'bill was' : 'bills were'} created${alreadyImported > 0 ? ` and ${String(alreadyImported)} already imported` : ''}.`
            : `Uploaded, with ${String(refused)} ${refused === 1 ? 'bill' : 'bills'} refused. Everything else in the file was recorded; put right what the refused bills name and upload the same file again.`}
        </AlertDescription>
      </Alert>

      <Tally counts={result.counts} preview={false} />

      <div className="flex flex-wrap gap-3">
        {result.id !== null && (
          <>
            <Button
              nativeButton={false}
              render={<Link to={machineBillsForImportPath(result.id)} />}
            >
              See its bills
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={machineImportPath(result.id)} />}
            >
              The upload&rsquo;s record
            </Button>
          </>
        )}
        <Button variant="outline" onClick={onAgain}>
          Upload another file
        </Button>
      </div>

      <Days days={result.days} preview={false} />
      <Bills bills={result.bills} preview={false} />
    </div>
  );
}

function WhatAFileIs() {
  return (
    <Band name="What a file is">
      <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
        <ScreenNote label="The machine's own export">
          ORDERS.CSV exactly as the machine wrote it. A file opened and saved again in a spreadsheet
          usually changes its columns, and is refused.
        </ScreenNote>
        <ScreenNote label="Up to 2 MB and 5,000 rows">
          A longer period is exported from the machine in parts and each part uploaded on its own.
        </ScreenNote>
        <ScreenNote label="One bad bill holds nothing back">
          A bill that cannot be recorded is listed with the reason and the rows it is on. The rest
          of the file is recorded either way.
        </ScreenNote>
        <ScreenNote label="Uploading again is safe">
          Bills already imported are recognised and not counted twice, so the same file can be
          uploaded again once a refused bill is put right.
        </ScreenNote>
      </div>
    </Band>
  );
}
